import { useMemo } from 'react'
import { formatCurrency } from '@/utils/format-currency'
import type { ComboVariation, PropostaItem } from '@/types'
import type { ComboValue } from './types'
import { buildProposalTotalsBreakdown } from './proposalTotalsBreakdown'

interface ProposalTotalsSectionProps {
  itens: PropostaItem[]
  comboValues: ComboValue[]
  comboVariations: ComboVariation[]
  desconto: number
}

function ItemLines({ items }: { items: PropostaItem[] }) {
  return (
    <ul className="space-y-0.5 pl-2 border-l border-white/[0.08]">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center justify-between gap-2 text-[10px] text-text-muted"
        >
          <span className="truncate min-w-0">{item.descricao}</span>
          <span className="shrink-0 tabular-nums text-text-label">
            {formatCurrency(item.total)}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function ProposalTotalsSection({
  itens,
  comboValues,
  comboVariations,
  desconto,
}: ProposalTotalsSectionProps) {
  const breakdown = useMemo(
    () => buildProposalTotalsBreakdown(itens, comboValues, comboVariations, desconto),
    [itens, comboValues, comboVariations, desconto],
  )

  if (itens.length === 0) return null

  const hasManualCombos = breakdown.comboLines.length > 0
  const hasVariations = breakdown.variationLines.length > 0
  const showAllItemsList =
    breakdown.displayMode === 'all_items' && breakdown.lineItems.length > 0

  return (
    <div className="space-y-2 pt-2 border-t border-white/[0.06]">
      <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider">
        Resumo de valores
      </h4>

      <div className="space-y-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
        {hasVariations && (
          <div className="space-y-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600/90">
              Opções montadas
            </span>
            {breakdown.variationLines.map((option) => (
              <div
                key={option.id}
                className="space-y-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold text-text-primary truncate min-w-0">
                    {option.label}
                  </span>
                  <span className="text-xs font-bold text-accent tabular-nums shrink-0">
                    {formatCurrency(option.scenarioTotal)}
                  </span>
                </div>
                <ItemLines items={option.items} />
                {option.desconto > 0 && (
                  <div className="flex items-center justify-between text-[10px] text-text-muted pl-2">
                    <span>Desconto na opção</span>
                    <span className="tabular-nums">
                      <span className="line-through mr-1.5">
                        {formatCurrency(option.valorOriginal)}
                      </span>
                      {formatCurrency(option.valorFinal)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {hasManualCombos && (
          <div
            className={
              hasVariations ? 'space-y-2 pt-2 border-t border-white/[0.06]' : 'space-y-2'
            }
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-accent">
              Combos com desconto
            </span>
            {breakdown.comboLines.map((combo) => (
              <div
                key={combo.id}
                className="space-y-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold text-text-primary truncate min-w-0">
                    {combo.label}
                  </span>
                  <span className="text-xs font-bold text-accent tabular-nums shrink-0">
                    {formatCurrency(combo.total)}
                  </span>
                </div>
                <ItemLines items={combo.items} />
                {combo.desconto > 0 && (
                  <div className="flex items-center justify-between text-[10px] text-text-muted pl-2">
                    <span>Desconto do combo</span>
                    <span className="tabular-nums">
                      <span className="line-through mr-1.5">
                        {formatCurrency(combo.subtotal)}
                      </span>
                      -{formatCurrency(combo.desconto)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {showAllItemsList && (
          <div
            className={
              hasVariations || hasManualCombos
                ? 'space-y-1 pt-2 border-t border-white/[0.06]'
                : 'space-y-1'
            }
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-text-label">
                Itens da proposta
              </span>
              <span className="text-xs font-medium text-text-primary tabular-nums">
                {formatCurrency(breakdown.lineItemsSubtotal)}
              </span>
            </div>
            <ItemLines items={breakdown.lineItems} />
          </div>
        )}

        {hasVariations && breakdown.lineItems.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-white/[0.06]">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-text-label">
                Incluso em todas as opções
              </span>
              <span className="text-xs font-medium text-text-primary tabular-nums">
                {formatCurrency(breakdown.lineItemsSubtotal)}
              </span>
            </div>
            <ItemLines items={breakdown.lineItems} />
          </div>
        )}

        {breakdown.mktItemsCount > 1 && breakdown.mktSelectedItem && (
          <p className="text-[10px] text-amber-600/90 dark:text-amber-400/90 pt-1 border-t border-white/[0.06]">
            Planos de marketing: no total entra só o de menor valor (
            <span className="font-medium">{breakdown.mktSelectedItem.descricao}</span>
            ).
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        {breakdown.desconto > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-label">Desconto geral ({breakdown.desconto}%)</span>
            <span className="text-sm text-red-400 tabular-nums">
              - {formatCurrency(breakdown.valorDesconto)}
            </span>
          </div>
        )}
        {breakdown.comboDiscountTotal > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-label">Economia nos combos</span>
            <span className="text-sm text-red-400 tabular-nums">
              - {formatCurrency(breakdown.comboDiscountTotal)}
            </span>
          </div>
        )}
        {hasVariations && breakdown.allItemsTotal > breakdown.displayTotal && (
          <div className="flex items-center justify-between text-[10px] text-text-muted">
            <span>Soma de todos os itens (referência)</span>
            <span className="line-through tabular-nums">{formatCurrency(breakdown.allItemsTotal)}</span>
          </div>
        )}
        <div className="flex items-start justify-between gap-3 pt-2 border-t border-white/[0.08]">
          <div className="min-w-0">
            <span className="text-xs font-bold text-text-primary uppercase tracking-wider block">
              {hasVariations ? 'Menor opção' : 'Total da proposta'}
            </span>
            <span className="text-[10px] text-text-muted leading-snug">
              {hasVariations && breakdown.minVariationLabel
                ? `Cenário "${breakdown.minVariationLabel}" + itens comuns, com descontos aplicados.`
                : hasManualCombos
                  ? 'Combos montados + demais itens, com descontos e regras aplicadas.'
                  : 'Soma dos itens, com descontos e regras aplicadas.'}
            </span>
          </div>
          <span className="text-lg font-bold text-accent tabular-nums shrink-0">
            {formatCurrency(breakdown.displayTotal)}
          </span>
        </div>
      </div>
    </div>
  )
}
